import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, PenLine, Trash2, Upload, User } from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import type { Medecin } from '../lib/types'
import { Carte, Champ, Chargement, Message } from '../composants/base'
import SeuilsAlerte from '../composants/SeuilsAlerte'
import TableConversions from '../composants/TableConversions'

export default function Parametres() {
  const client = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['moi'],
    queryFn: () => api<{ medecin: Medecin }>('/api/moi'),
  })

  const [brouillon, setBrouillon] = useState<Partial<Medecin> | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enregistre, setEnregistre] = useState(false)

  const medecin = { ...data?.medecin, ...brouillon } as Medecin | undefined

  const enregistrer = useMutation({
    mutationFn: () =>
      api<{ medecin: Medecin }>('/api/moi', {
        methode: 'PATCH',
        corps: {
          civilite: medecin?.civilite ?? null,
          nom: medecin?.nom ?? '',
          prenom: medecin?.prenom ?? '',
          specialite: medecin?.specialite ?? null,
          rpps: medecin?.rpps ?? null,
          adeli: medecin?.adeli ?? null,
          adresseLigne1: medecin?.adresseLigne1 ?? null,
          adresseLigne2: medecin?.adresseLigne2 ?? null,
          codePostal: medecin?.codePostal ?? null,
          ville: medecin?.ville ?? null,
          telephone: medecin?.telephone ?? null,
          emailPro: medecin?.emailPro ?? null,
        },
      }),
    onSuccess: () => {
      setErreur(null)
      setEnregistre(true)
      setTimeout(() => setEnregistre(false), 2500)
      client.invalidateQueries({ queryKey: ['moi'] })
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const deposerSignature = useMutation({
    mutationFn: (fichier: File) => {
      const formulaire = new FormData()
      formulaire.append('fichier', fichier)
      return api('/api/moi/signature', { methode: 'POST', formulaire })
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['moi'] }),
    onError: (e) => setErreur(messageErreur(e)),
  })

  const retirerSignature = useMutation({
    mutationFn: () => api('/api/moi/signature', { methode: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['moi'] }),
  })

  if (isLoading || !medecin) return <Chargement />

  const champ = (cle: keyof Medecin) => ({
    value: (medecin[cle] as string | null) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setBrouillon((b) => ({ ...b, [cle]: e.target.value })),
  })

  return (
    <div className="mx-auto max-w-[840px] pt-1">
      <h1 className="mb-4 text-[20px] font-semibold tracking-tight">Paramètres</h1>

      <div className="flex flex-col gap-5">
        <Carte titre="Identité et prescripteur" icone={<User className="h-[17px] w-[17px] text-bleu-500" />}>
          <div className="px-5 pb-5">
            <p className="mb-4 text-[13px] text-brume">
              Ces informations seront reprises sur les documents générés : ordonnances, fiche de
              bloc, bon d’anatomo-pathologie.
            </p>

            <form
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault()
                enregistrer.mutate()
              }}
            >
              <Champ etiquette="Civilité">
                <input className="champ" placeholder="Dr" {...champ('civilite')} />
              </Champ>
              <Champ etiquette="Spécialité">
                <input
                  className="champ"
                  placeholder="Gynécologie-obstétrique"
                  {...champ('specialite')}
                />
              </Champ>
              <Champ etiquette="Prénom">
                <input className="champ" required {...champ('prenom')} />
              </Champ>
              <Champ etiquette="Nom">
                <input className="champ" required {...champ('nom')} />
              </Champ>
              <Champ etiquette="Numéro RPPS">
                <input className="champ" {...champ('rpps')} />
              </Champ>
              <Champ etiquette="Numéro ADELI">
                <input className="champ" {...champ('adeli')} />
              </Champ>
              <Champ etiquette="Adresse professionnelle" className="sm:col-span-2">
                <input className="champ" {...champ('adresseLigne1')} />
              </Champ>
              <Champ etiquette="Complément d’adresse" className="sm:col-span-2">
                <input className="champ" {...champ('adresseLigne2')} />
              </Champ>
              <Champ etiquette="Code postal">
                <input className="champ" {...champ('codePostal')} />
              </Champ>
              <Champ etiquette="Ville">
                <input className="champ" {...champ('ville')} />
              </Champ>
              <Champ etiquette="Téléphone">
                <input className="champ" {...champ('telephone')} />
              </Champ>
              <Champ etiquette="E-mail professionnel" aide="Affiché sur les documents générés.">
                <input className="champ" {...champ('emailPro')} />
              </Champ>

              {erreur && (
                <div className="sm:col-span-2">
                  <Message ton="critique">{erreur}</Message>
                </div>
              )}

              <div className="flex items-center gap-3 sm:col-span-2">
                <button className="bouton-primaire" disabled={enregistrer.isPending}>
                  Enregistrer
                </button>
                {enregistre && (
                  <span className="flex items-center gap-1.5 text-[13px] text-valide">
                    <Check className="h-4 w-4" />
                    Enregistré
                  </span>
                )}
              </div>
            </form>
          </div>
        </Carte>

        <Carte
          titre="Image de signature"
          icone={<PenLine className="h-[17px] w-[17px] text-bleu-500" />}
        >
          <div className="px-5 pb-5">
            <p className="mb-3 text-[13px] text-brume">
              Une image apposée sur les documents générés. Ce n’est pas une signature électronique :
              elle ne vaut pas preuve d’authenticité, et le cahier des charges n’en demande pas.
            </p>

            {medecin.signature ? (
              <div className="flex items-center gap-4">
                <img
                  src={`/api/moi/signature?t=${Date.now()}`}
                  alt="Signature"
                  className="h-20 rounded-lg bg-slate-50 object-contain px-3"
                />
                <button
                  className="bouton-discret text-critique"
                  onClick={() => retirerSignature.mutate()}
                >
                  <Trash2 className="h-4 w-4" />
                  Retirer
                </button>
              </div>
            ) : (
              <label className="bouton-doux w-fit cursor-pointer">
                <Upload className="h-4 w-4" />
                Déposer une image (PNG, JPEG, WebP)
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const fichier = e.target.files?.[0]
                    if (fichier) deposerSignature.mutate(fichier)
                  }}
                />
              </label>
            )}
          </div>
        </Carte>

        <SeuilsAlerte />

        <TableConversions />

        <Carte titre="Modèles de documents">
          <div className="px-5 pb-5">
            <Message ton="info">
              Modèles d’ordonnance, modèle du Centre Georges François Leclerc, blocs de prescription
              réutilisables : phase 4. Tu as prévu de fournir les modèles.
            </Message>
          </div>
        </Carte>
      </div>
    </div>
  )
}
